import { useCallback, useContext, useEffect, useState } from "react";
import {
  ButtonGroup,
  ButtonToolbar,
  Col,
  Form,
  Row,
  ProgressBar,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import type { UnpackResponse } from "../../../api/src/lib";
import type { ReportsController } from "../../../api/src/reports/reports.controller";
import ActionButton from "../components/ActionButton";
import ConfirmationDialog from "../components/ConfirmationDialog";
import Content from "../components/Content";
import FormError from "../components/FormError";
import { useAuthenticatedMutationNew } from "../lib/api";
import { boolToYesNo, capitalize } from "../lib/display";
import { processFileResponse } from "../lib/file";
import Loading from "../Loading";
import { NotificationContext } from "../Notifications";
import { useDeleteReportBatch, useQueryReportBatch } from "./lib";
import ReportsTable from "./ReportsTable";

interface ReportProgress {
  reportBatchId: number;
  bridgeJobId: string;
  current_step: number;
  num_steps: number;
  progress_percentage: number;
}

export function useBulkDownloadReports() {
  return useAuthenticatedMutationNew<
    UnpackResponse<ReportsController["getMultipleReports"]>,
    void,
    { reportIds: number[] }
  >({
    mutationKey: ["POST", "reports", "bulk"],
    mutationFn: () => null,
    responseCallback: processFileResponse,
  });
}

const ReportBatchInfo = () => {
  const { reportBatchId } = useParams();
  const navigate = useNavigate();
  const [progress, setProgress] = useState<ReportProgress | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const {
    isFetched,
    isError,
    data: reportBatch,
  } = useQueryReportBatch(parseInt(reportBatchId ?? ""));

  const notificationContext = useContext(NotificationContext);
  const downloadReportsMutation = useBulkDownloadReports();

  // Listen for progress events from WebSocket
  useEffect(() => {
    const handleProgressEvent = (event: CustomEvent<ReportProgress>) => {
      const progressData = event.detail;
      if (progressData.reportBatchId === parseInt(reportBatchId ?? "")) {
        setProgress(progressData);
      }
    };

    window.addEventListener("reportProgress" as any, handleProgressEvent);

    return () => {
      window.removeEventListener("reportProgress" as any, handleProgressEvent);
    };
  }, [reportBatchId]);

  // Clear progress when report batch status is not pending
  useEffect(() => {
    if (reportBatch?.status !== "pending") {
      setProgress(null);
    }
  }, [reportBatch?.status]);

  const onDownload = useCallback(async () => {
    try {
      await downloadReportsMutation.mutateAsync({
        reportIds: (reportBatch?.reports ?? []).map((report) => report.id),
      });
      notificationContext.pushNotification({
        id: "report-bulk",
        header: "Reports downloaded",
        body: "Reports downloaded successfully",
        variant: "success",
      });
    } catch (ex) {
      console.error("Failed to download reports", ex);
      notificationContext.pushNotification({
        id: "report-bulk",
        header: "Failed to download reports",
        body: "An error occurred",
        variant: "danger",
      });
    }
  }, [downloadReportsMutation, notificationContext, reportBatch?.reports]);

  const deleteReportBatch = useDeleteReportBatch(parseInt(reportBatchId ?? ""));

  const onDelete = useCallback(async () => {
    try {
      await deleteReportBatch.mutateAsync();
    } catch (ex) {
      console.error("Failed to delete report batch", ex);
      setShowDeleteConfirm(false);
      notificationContext.pushNotification({
        id: `report-batch-${reportBatchId}`,
        header: "Failed to Delete Report Batch",
        body: `Report batch for ${reportBatch?.reportDate.toLocaleDateString()} not deleted`,
        variant: "danger",
      });
      return;
    }
    setShowDeleteConfirm(false);
    notificationContext.pushNotification({
      id: `report-batch-${reportBatchId}`,
      header: "Report Batch Deleted",
      body: `Report batch for ${reportBatch?.reportDate.toLocaleDateString()} deleted`,
      variant: "warning",
    });
    navigate("..");
  }, [
    deleteReportBatch,
    navigate,
    notificationContext,
    reportBatch?.reportDate,
    reportBatchId,
  ]);

  // The dialog stays open while the delete runs (R44).
  const onCloseDeleteConfirm = useCallback(() => {
    if (!deleteReportBatch.isPending) setShowDeleteConfirm(false);
  }, [deleteReportBatch.isPending]);

  return !isFetched ? (
    <Loading />
  ) : isError ? (
    <FormError message="Failed to load reports" />
  ) : typeof reportBatch === "undefined" ? (
    <FormError message="Report not found" />
  ) : (
    <>
      <Row>
        <Col>
          <h1>Report Details</h1>
        </Col>
        <Col md="auto">
          <ButtonToolbar className="gap-3 mb-3 justify-content-end">
            <ButtonGroup>
              <ActionButton
                variant="secondary"
                label="Download"
                icon="/icons/folded-list.svg"
                onClick={onDownload}
                disabled={
                  downloadReportsMutation.isPending ||
                  reportBatch.reports?.length === 0
                }
              />
            </ButtonGroup>
            <ButtonGroup>
              <ActionButton
                variant="secondary"
                label="Delete"
                icon="/icons/trash.svg"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleteReportBatch.isPending}
              />
            </ButtonGroup>
          </ButtonToolbar>
        </Col>
      </Row>
      <ConfirmationDialog
        title="Delete report batch?"
        message={`Report batch for ${reportBatch.reportDate.toLocaleDateString()} will be deleted.`}
        showModal={showDeleteConfirm}
        isConfirming={deleteReportBatch.isPending}
        handleConfirm={onDelete}
        handleClose={onCloseDeleteConfirm}
        confirmLabel="Delete"
      />
      <Content>
        <Row>
          <Col xxl={2} md={4} sm={6} className="mb-2">
            <Form.Group controlId="form-name">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={reportBatch.name}
                plaintext
                readOnly
              />
            </Form.Group>
          </Col>
          <Col xxl={2} md={4} sm={6} className="mb-2">
            <Form.Group controlId="form-period">
              <Form.Label>Report Date</Form.Label>
              <Form.Control
                type="text"
                value={reportBatch.reportDate.toLocaleDateString()}
                plaintext
                readOnly
              />
            </Form.Group>
          </Col>
          <Col xxl={2} md={4} sm={6} className="mb-2">
            <Form.Group controlId="form-period">
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="text"
                value={reportBatch.startDate.toLocaleDateString()}
                plaintext
                readOnly
              />
            </Form.Group>
          </Col>
          <Col xxl={2} md={4} sm={6} className="mb-2">
            <Form.Group controlId="form-period">
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="text"
                value={reportBatch.endDate.toLocaleDateString()}
                plaintext
                readOnly
              />
            </Form.Group>
          </Col>
          <Col xl={2} md={4} sm={6} className="mb-2">
            <Form.Group controlId="form-createdTime">
              <Form.Label>Created Date</Form.Label>
              <Form.Control
                type="text"
                value={reportBatch.createdTime.toLocaleString()}
                plaintext
                readOnly
              />
            </Form.Group>
          </Col>
          <Col xl={2} md={4} sm={6} className="mb-2">
            <Form.Group controlId="form-status">
              <Form.Label>Status</Form.Label>
              <Form.Control
                type="text"
                value={capitalize(reportBatch.status)}
                plaintext
                readOnly
              />
            </Form.Group>
          </Col>
          {progress && (
            <Col xl={4} md={8} sm={12} className="mb-2">
              <Form.Group controlId="form-progress">
                <ProgressBar
                  now={progress.progress_percentage}
                  label={`${progress.progress_percentage}%`}
                  variant="info"
                />
              </Form.Group>
            </Col>
          )}
        </Row>
        {reportBatch.status === "complete" && (
          <>
            <Row>
              <h2>Reports</h2>
            </Row>
            <Row>
              <Col>
                <ReportsTable
                  reports={reportBatch.reports ?? []}
                  batchId={parseInt(reportBatchId ?? "")}
                />
              </Col>
            </Row>
          </>
        )}
      </Content>
    </>
  );
};

export default ReportBatchInfo;
